import { type App, type Plugin, Route, View } from "@pimo/pimo-app-builder";
import { DoubleNumberCardV2, GridLayout } from "@pimo/pimo-components";
import {
  AggregatedImpactValues,
  FUNCTION_TYPES,
  IMPLEMENTATION_PROGRAM_TYPES,
  INITIATIVE_PHASES,
  Phase,
} from "aint-types";
import {
  formatDate,
  getColorsAndTextForInitiativePhaseChips,
  getUpdateChipColorBasedOnUpdateStatus,
  IN_COLORS,
  OES_WITH_DEPENDENT_FIELDS,
} from "aint-utils";

import AintInitiativesTable from "../../components/aint-initiatives-table/aint-initiatives-table.tsx";
import { DashboardTitleCard } from "../../components/dashboard-title-card/dashboard-title-card.tsx";
import { LineChartCard } from "../../components/line-chart-card/line-chart-card.tsx";
import { AINTSectionHeader } from "../../components/section-header/section-header.tsx";
import { FORM_FIELD_INFO_TEXT } from "../../form/form-field-info-text.ts";
import { InAppState } from "../app";
import { APP_ROUTES } from "../constants";
import { fetchAllRegions, fetchOEs } from "../helpers/fetch-helpers.ts";
import { theme } from "../theme.ts";
import { convertNumberToMillions } from "../utils/value-converters.ts";

export class DashboardPlugin implements Plugin<InAppState, InAppState> {
  route?: Route;
  private view?: View<InAppState, GridLayout>;
  private app?: App<InAppState>;

  onRegister(app: App<InAppState>): void {
    this.app = app;
    this.view = app.createView({
      name: "Dashboard",
      layout: new GridLayout(2, false, 3),
    });
    this.buildRoute();
    this.buildDashboardView(this.view);
  }

  private buildDashboardView(view: View<InAppState, GridLayout>) {
    const viewTitle = view.addComponent({
      component: DashboardTitleCard,
      layoutProps: {
        xs: 12,
      },
    });

    viewTitle.mapState(
      ({
        groupOverview,
        dashboardFilterDialogOptions,
        dashboardFilterDialogData,
        currency,
        userProfile,
      }) => {
        const fromFilter = dashboardFilterDialogData.fromDateFilter?.[0];
        const untilFilter = dashboardFilterDialogData.untilDateFilter?.[0];
        const from = formatDate(
          fromFilter
            ? fromFilter
            : (groupOverview?.impactMetrics?.startDate ?? new Date()),
          "YYYY-MM"
        );
        const until = formatDate(
          untilFilter
            ? untilFilter
            : (groupOverview?.impactMetrics?.endDate ?? new Date()),
          "YYYY-MM"
        );

        return {
          cardProps: {
            sx: {
              color: theme.palette.primary.contrastText,
            },
          },
          userProfile,
          localCurrency:
            groupOverview?.oes?.length === 1
              ? groupOverview?.oes?.[0]?.currency
                ? groupOverview?.oes?.[0]?.currency
                : "EUR"
              : "EUR",
          selectedCurrency: groupOverview?.oes?.length === 1 ? currency : "EUR",
          title: from && until ? `Dashboard (${from} - ${until})` : "Dashboard",
          filterOptions: dashboardFilterDialogOptions,
          filterData: dashboardFilterDialogData,
        };
      }
    );

    viewTitle.on("filter:apply", async (payload) => {
      if (!payload.payload || typeof payload.payload === "string") {
        return;
      }

      const [groupOverview, oes, regions] = await Promise.all([
        fetchOEs(
          payload.payload,
          payload.payload.oeProjectsFilter?.length === 1
            ? this.app?.getAppState().currency
            : "EUR"
        ),
        fetchOEs(),
        fetchAllRegions({ oeProjectsFilter: payload.payload.oeProjectsFilter }),
      ]);
      const implementationPrograms: (
        | (typeof IMPLEMENTATION_PROGRAM_TYPES)[number]
        | "Elevate"
      )[] = IMPLEMENTATION_PROGRAM_TYPES.map((type) => type);
      implementationPrograms.splice(-1, 0, "Elevate");

      this.app?.patchAppState({
        groupOverview,
        dashboardFilterDialogData: payload.payload,
        currency:
          groupOverview?.oes?.length === 1
            ? this.app?.getAppState().currency
            : "EUR",
        dashboardFilterDialogOptions: {
          oeProjects: oes.oeOverview.map((oe) => oe.oeName),
          regions: regions.map(({ regionName }) => regionName),
          implementationPrograms,
        },
      });
    });

    viewTitle.on("filter:clear", async () => {
      const groupOverview = await fetchOEs();

      this.app?.patchAppState({
        groupOverview,

        dashboardFilterDialogData: {
          oeProjectsFilter: [],
          regionsFilter: [],
          LoBsFilter: [],
          functionsFilter: [],
          programsFilter: [],
          enablersFilter: [],
          baselinesFilter: [],
          fromDateFilter: [],
          untilDateFilter: [],
        },
      });
    });

    viewTitle.on("currency:change", async (payload) => {
      if (!payload.payload || typeof payload.payload !== "string") {
        return;
      }

      // Get the current filter dialog data from the app state to retain oeProjectsFilter
      const currentFilterDialogData =
        this.app?.getAppState()?.dashboardFilterDialogData ?? {};
      const oeProjectsFilter = currentFilterDialogData.oeProjectsFilter ?? [];

      // Fetch the group overview with the current oeProjectsFilter and the new currency
      const groupOverview = await fetchOEs(
        {
          oeProjectsFilter,
        },
        payload.payload // This is now the currency string
      );

      // Patch the app state with the new group overview
      this.app?.patchAppState({
        groupOverview,
        currency: payload.payload,
      });
    });

    const monthlyImpactSectionHeader = view.addComponent({
      component: AINTSectionHeader,
      layoutProps: {
        xs: 12,
      },
    });
    monthlyImpactSectionHeader.mapState(() => ({
      title: "Monthly Impact Metrics",
    }));

    const totalNetBenefitsCard = view.addComponent({
      component: DoubleNumberCardV2,
      layoutProps: {
        xs: 12,
        sm: 12 / 2,
        md: 12 / 4,
      },
    });

    totalNetBenefitsCard.mapState(({ groupOverview, currency }) => {
      return {
        cardProps: {
          sx: {
            border: "1.5px solid",
            borderColor: theme.palette.primary.main,
          },
        },
        title: "Total Net Benefits",
        infoBoxText: FORM_FIELD_INFO_TEXT["totalNetBenefits"],
        textColor: theme.palette.primary.main,
        content1: {
          value: `${
            convertNumberToMillions(
              groupOverview?.impactMetrics?.metrics?.totalImpact?.actual ?? 0
            )?.toLocaleString("en-US") ?? "0"
          } Mn ${currency ?? "EUR"}`,
          label: "Actual",
        },
        content2: {
          value: `${
            convertNumberToMillions(
              groupOverview?.impactMetrics?.metrics?.totalImpact?.planned ?? 0
            )?.toLocaleString("en-US") ?? "0"
          } Mn ${currency ?? "EUR"}`,
          label: "Planned",
        },
      };
    });

    const totalTIEServiceExpCard = view.addComponent({
      component: DoubleNumberCardV2,
      layoutProps: {
        xs: 12,
        sm: 12 / 2,
        md: 12 / 4,
      },
    });

    totalTIEServiceExpCard.mapState(({ groupOverview, currency }) => {
      return {
        title: "Total TIE & Service Exp",
        infoBoxText: FORM_FIELD_INFO_TEXT["tieServiceExpensesImpactActual"],
        textColor: theme.palette.primary.main,
        content1: {
          value: `${
            convertNumberToMillions(
              groupOverview?.impactMetrics?.metrics?.tieServiceExpensesImpact
                ?.actual ?? 0
            )?.toLocaleString("en-US") ?? "0"
          } Mn ${currency ?? "EUR"}`,
          label: "Actual",
        },
        content2: {
          value: `${
            convertNumberToMillions(
              groupOverview?.impactMetrics?.metrics?.tieServiceExpensesImpact
                ?.planned ?? 0
            )?.toLocaleString("en-US") ?? "0"
          } Mn ${currency ?? "EUR"}`,
          label: "Planned",
        },
      };
    });

    const totalIndemnityImpactCard = view.addComponent({
      component: DoubleNumberCardV2,
      layoutProps: {
        xs: 12,
        sm: 12 / 2,
        md: 12 / 4,
      },
    });

    totalIndemnityImpactCard.mapState(({ groupOverview, currency }) => {
      return {
        title: "Total Indemnity Impact",
        infoBoxText: FORM_FIELD_INFO_TEXT["indemnityImpactActual"],
        textColor: theme.palette.primary.main,
        content1: {
          value: `${
            convertNumberToMillions(
              groupOverview?.impactMetrics?.metrics?.indemnityImpact?.actual ??
                0
            )?.toLocaleString("en-US") ?? "0"
          } Mn ${currency ?? "EUR"}`,
          label: "Actual",
        },
        content2: {
          value: `${
            convertNumberToMillions(
              groupOverview?.impactMetrics?.metrics?.indemnityImpact?.planned ??
                0
            )?.toLocaleString("en-US") ?? "0"
          } Mn ${currency ?? "EUR"}`,
          label: "Planned",
        },
      };
    });

    const totalToplineImpactCard = view.addComponent({
      component: DoubleNumberCardV2,
      layoutProps: {
        xs: 12,
        sm: 12 / 2,
        md: 12 / 4,
      },
    });

    totalToplineImpactCard.mapState(({ groupOverview, currency }) => {
      return {
        title: "Total Topline Impact",
        infoBoxText: FORM_FIELD_INFO_TEXT["toplineImpactActual"],
        textColor: theme.palette.primary.main,
        content1: {
          value: `${
            convertNumberToMillions(
              groupOverview?.impactMetrics?.metrics?.toplineImpact?.actual ?? 0
            )?.toLocaleString("en-US") ?? "0"
          } Mn ${currency ?? "EUR"}`,
          label: "Actual",
        },
        content2: {
          value: `${
            convertNumberToMillions(
              groupOverview?.impactMetrics?.metrics?.toplineImpact?.planned ?? 0
            )?.toLocaleString("en-US") ?? "0"
          } Mn ${currency ?? "EUR"}`,
          label: "Planned",
        },
      };
    });

    const totalCommissionsImpactCard = view.addComponent({
      component: DoubleNumberCardV2,
      layoutProps: {
        xs: 12,
        sm: 12 / 2,
        md: 12 / 4,
      },
    });

    totalCommissionsImpactCard.mapState(({ groupOverview, currency }) => {
      return {
        title: "Total Comissions Impact",
        textColor: theme.palette.primary.main,
        infoBoxText: FORM_FIELD_INFO_TEXT["commissionsImpactActual"],
        content1: {
          value: `${
            convertNumberToMillions(
              groupOverview?.impactMetrics?.metrics?.commissionsImpact
                ?.actual ?? 0
            )?.toLocaleString("en-US") ?? "0"
          } Mn ${currency ?? "EUR"}`,
          label: "Actual",
        },
        content2: {
          value: `${
            convertNumberToMillions(
              groupOverview?.impactMetrics?.metrics?.commissionsImpact
                ?.planned ?? 0
            )?.toLocaleString("en-US") ?? "0"
          } Mn ${currency ?? "EUR"}`,

          label: "Planned",
        },
      };
    });

    const internalFTEImpactCard = view.addComponent({
      component: DoubleNumberCardV2,
      layoutProps: {
        xs: 12,
        sm: 12 / 2,
        md: 12 / 4,
      },
    });

    internalFTEImpactCard.mapState(({ groupOverview }) => {
      return {
        title: "Internal FTE Impact",
        textColor: theme.palette.primary.main,
        infoBoxText: FORM_FIELD_INFO_TEXT["internalFteImpactActual"],
        content1: {
          value: `${
            Math.round(
              (groupOverview?.impactMetrics?.metrics?.internalFteImpact
                ?.actual ?? 0) * 100
            ) / 100
          }`,
          label: "Actual",
        },
        content2: {
          value: `${
            Math.round(
              (groupOverview?.impactMetrics?.metrics?.internalFteImpact
                ?.planned ?? 0) * 100
            ) / 100
          }`,
          label: "Planned",
        },
      };
    });

    const externallFTEImpactCard = view.addComponent({
      component: DoubleNumberCardV2,
      layoutProps: {
        xs: 12,
        sm: 12 / 2,
        md: 12 / 4,
      },
    });

    externallFTEImpactCard.mapState(({ groupOverview }) => {
      return {
        title: "External FTE Impact",
        textColor: theme.palette.primary.main,
        infoBoxText: FORM_FIELD_INFO_TEXT["externalFteImpactActual"],
        content1: {
          value: `${
            Math.round(
              (groupOverview?.impactMetrics?.metrics?.externalFteImpact
                ?.actual ?? 0) * 100
            ) / 100
          }`,
          label: "Actual",
        },
        content2: {
          value: `${
            Math.round(
              (groupOverview?.impactMetrics?.metrics?.externalFteImpact
                ?.planned ?? 0) * 100
            ) / 100
          }`,
          label: "Planned",
        },
      };
    });

    const totalInvestmentCard = view.addComponent({
      component: DoubleNumberCardV2,
      layoutProps: {
        xs: 12,
        sm: 12 / 2,
        md: 12 / 4,
      },
    });

    totalInvestmentCard.mapState(({ groupOverview, currency }) => {
      return {
        title: "Total Investment",
        textColor: theme.palette.primary.main,
        infoBoxText: FORM_FIELD_INFO_TEXT["investmentActual"],
        content1: {
          value: `${
            convertNumberToMillions(
              groupOverview?.impactMetrics?.metrics?.investment?.actual ?? 0
            )?.toLocaleString("en-US") ?? "0"
          } Mn ${currency ?? "EUR"}`,
          label: "Actual",
        },
        content2: {
          value: `${
            convertNumberToMillions(
              groupOverview?.impactMetrics?.metrics?.investment?.planned ?? 0
            )?.toLocaleString("en-US") ?? "0"
          } Mn ${currency ?? "EUR"}`,
          label: "Planned",
        },
      };
    });

    const totalBenefitsChartCard = view.addComponent({
      component: LineChartCard,
      layoutProps: {
        xs: 12,
        md: 6,
      },
    });

    totalBenefitsChartCard.mapState((appState) => {
      const { totalImpact, startMonth, startYear } =
        appState.groupOverview?.groupImpactValues ?? {};

      return {
        cardProps: {
          sx: { color: theme.palette.primary.contrastText },
        },
        initialMonth: startMonth ?? 0,
        currency: appState.currency ?? "EUR",
        initialYear: startYear ?? "",
        cardTitle: "Total Net Benefits (Actual/Planned)",
        title: "Total Net Benefits (Actual/Planned)",
        series: [
          {
            id: "totalImpact",
            label: "Total Net Benefits",
            data: totalImpact ?? [],
            color: IN_COLORS.forestGreen,
            curve: "linear",
          },
        ],
      };
    });

    const detailedImpactChartCard = view.addComponent({
      component: LineChartCard,
      layoutProps: {
        xs: 12,
        md: 6,
      },
    });

    detailedImpactChartCard.mapState((appState) => {
      const aggregatedImpactValues: AggregatedImpactValues = appState
        .groupOverview?.groupImpactValues || {
        totalImpact: [],
        tieServiceExpensesImpact: [],
        commissionsImpact: [],
        indemnityImpact: [],
        toplineImpact: [],
        investment: [],
        startMonth: 0,
        startYear: "",
      };

      const { startMonth, startYear } = aggregatedImpactValues;
      const SERIES = [
        {
          id: "tieServiceExpensesImpact",
          label: "Tie & Service Expenses Impact",
          color: IN_COLORS.grey,
        },
        {
          id: "indemnityImpact",
          label: "Indemnity Impact",
          color: IN_COLORS.deepBlue,
        },
        {
          id: "toplineImpact",
          label: "Topline Impact",
          color: IN_COLORS.navyBlue,
        },
        {
          id: "commissionsImpact",
          label: "Commisions Impact",
          color: IN_COLORS.orchid,
        },
        {
          id: "investment",
          label: "Investment",
          color: IN_COLORS.green,
        },
      ];

      return {
        cardProps: {
          sx: { color: theme.palette.primary.contrastText },
        },
        initialMonth: startMonth,
        initialYear: startYear,
        currency: appState.currency ?? "EUR",
        cardTitle: "Detailed Benefits (Actual/Planned)",
        title: "Detailed Impact (Actual/Planned)",
        showSeriesFilter: true,
        series: SERIES.map((series) => ({
          ...series,
          data: aggregatedImpactValues[
            series.id as keyof AggregatedImpactValues
          ] as (number | null)[],
          curve: "linear",
        })),
      };
    });

    const initiativesSectionHeader = view.addComponent({
      component: AINTSectionHeader,
      layoutProps: {
        xs: 12,
      },
    });
    initiativesSectionHeader.mapState(() => ({
      title: "Initiatives",
    }));

    const initiativesTable = view.addComponent({
      component: AintInitiativesTable,
      layoutProps: {
        xs: 12,
      },
    });

    initiativesTable.mapState((state) => {
      const allInitiatives = state.groupOverview?.initiatives ?? [];

      const userProfile = state.userProfile;

      const hasAllianzPartnersAccess =
        userProfile?.role === "editor" || userProfile?.role === "viewer"
          ? userProfile.oes?.some(
              (oe) => oe.operatingEntity === "Allianz Partners"
            ) ||
            userProfile.regions?.some((region) =>
              region.oes?.some(
                (oe) => oe.operatingEntity === "Allianz Partners"
              )
            )
          : false;

      const shouldAlwaysShowOtherFunction =
        userProfile?.role === "admin" ||
        userProfile?.role === "globalViewer" ||
        hasAllianzPartnersAccess;

      const functionTypesToDisplay = FUNCTION_TYPES.filter((functionType) => {
        if (functionType !== "Other") {
          return true;
        }

        if (shouldAlwaysShowOtherFunction) {
          return true;
        }

        return allInitiatives.some((initiative) => {
          const primary = initiative?.primaryFunction;
          const values = Array.isArray(primary)
            ? primary
            : primary
              ? [primary]
              : [];
          return values.includes("Other");
        });
      });

      const initiativesByFunction = functionTypesToDisplay.map(
        (functionType) => {
          const initiatives = allInitiatives.filter((initiative) => {
            const primary = initiative?.primaryFunction;
            const values = Array.isArray(primary)
              ? primary
              : primary
                ? [primary]
                : [];
            return values.includes(functionType);
          });
          const phaseCounts: Record<string, number> = {};
          for (const phase of INITIATIVE_PHASES) {
            const initiativesByPhaseCount = initiatives.filter(
              (initiative) => initiative.initiativePhase === phase
            ).length;
            phaseCounts[phase] = initiativesByPhaseCount;
          }
          return {
            function: functionType,
            phases: Object.entries(phaseCounts).map(([phase, count]) => ({
              count,
              phaseName:
                getColorsAndTextForInitiativePhaseChips(
                  phase as unknown as Phase
                )?.label ?? "",
              phaseColor:
                getColorsAndTextForInitiativePhaseChips(
                  phase as unknown as Phase
                )?.chipColor ?? "#FFF",
              textColor:
                getColorsAndTextForInitiativePhaseChips(
                  phase as unknown as Phase
                )?.textColor ?? "#000",
            })),

            initiatives: initiatives.map((initiative) => ({
              initiative,
              name: initiative.initiativeName,
              projectStartDate: formatDate(initiative.startDate, "MM/YYYY"),
              monitoringEndDate: formatDate(
                initiative.monitoringEndDate,
                "MM/YYYY"
              ),
              actualBenefits:
                initiative.realizedImpact == null
                  ? "n/a"
                  : `${convertNumberToMillions(initiative.realizedImpact ?? 0)?.toLocaleString("en-US")} Mn ${
                      state.currency ?? "EUR"
                    }`,

              plannedBenefits:
                initiative.forecastImpact == null
                  ? "n/a"
                  : `${convertNumberToMillions(initiative.forecastImpact ?? 0)?.toLocaleString("en-US")} Mn ${
                      state.currency ?? "EUR"
                    }`,
              phase: {
                body:
                  getColorsAndTextForInitiativePhaseChips(
                    initiative.initiativePhase as unknown as Phase
                  )?.label ?? "",
                chipProps: {
                  backgroundColor:
                    getColorsAndTextForInitiativePhaseChips(
                      initiative.initiativePhase as unknown as Phase
                    )?.chipColor ?? "#FFF",
                  color:
                    getColorsAndTextForInitiativePhaseChips(
                      initiative.initiativePhase as unknown as Phase
                    )?.textColor ?? "#000",
                },
              },
              reportingDetails: {
                body: `Due: ${formatDate(initiative.dueDate)}`,
                icon: getUpdateChipColorBasedOnUpdateStatus(
                  initiative.updateStatus ?? "completed"
                )?.icon,
                chipColor: getUpdateChipColorBasedOnUpdateStatus(
                  initiative.updateStatus ?? "completed"
                )?.color,
              },
            })),
          };
        }
      );
      const currentOE = state.currentOE;
      const dependentFields =
        OES_WITH_DEPENDENT_FIELDS[
          currentOE?.operatingEntity as keyof typeof OES_WITH_DEPENDENT_FIELDS
        ] ?? ([] as string[]);

      return {
        currency: state.currency,
        functions: initiativesByFunction,
        showInterOeRegionField: dependentFields.includes("interOeRegion"),
        showBusinessUnitField: dependentFields.includes("businessUnit"),
        showCategoryField: dependentFields.includes("category"),
        showOeProgramFields: dependentFields.includes("oeProgram"),
      };
    });
  }

  private buildRoute() {
    if (!this.app || !this.view) {
      return;
    }

    this.route = this.app.createRoute({
      path: `${APP_ROUTES.dashboard}`,
      view: this.view,
    });

    this.route?.on("load", async () => {
      if (!this.app) {
        return;
      }

      const {
        isLoggedIn,
        dashboardFilterDialogData: { regionsFilter, oeProjectsFilter },
      } = this.app.getAppState();

      if (!isLoggedIn) {
        this.app.navigate(APP_ROUTES.login);
        return;
      }

      const [groupOverview, allRegions] = await Promise.all([
        fetchOEs({ regionsFilter: regionsFilter }),
        fetchAllRegions({ oeProjectsFilter }),
      ]);

      const implementationPrograms: (
        | (typeof IMPLEMENTATION_PROGRAM_TYPES)[number]
        | "Elevate"
      )[] = IMPLEMENTATION_PROGRAM_TYPES.map((type) => type);
      implementationPrograms.splice(-1, 0, "Elevate");

      this.app.patchAppState({
        groupOverview,
        isLoading: false,
        dashboardFilterDialogOptions: {
          oeProjects: groupOverview.oeOverview.map((oe) => oe.oeName),
          regions: allRegions.map(({ regionName }) => regionName),
          implementationPrograms,
        },
        currency: "EUR",
        dashboardFilterDialogData: {
          oeProjectsFilter: [],
          regionsFilter: [],
          LoBsFilter: [],
          functionsFilter: [],
          programsFilter: [],
          enablersFilter: [],
          baselinesFilter: [],
          fromDateFilter: [],
          untilDateFilter: [],
        },
      });
    });
  }
}
